// src/pages/CentroCommercialePage.js
// Sezione commerciale di Nexiora Insight — un solo numero in evidenza, subito visibile:
// il margine reale delle offerte finalizzate. Tutto il resto è di supporto, non al centro.
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useOrg } from "../contexts/OrgContext";
import { TrendingUp, FileText, AlertTriangle } from "lucide-react";
import { calcolaTotaliEO } from "../components/ElaborazioneOffertaTab";
import { calcolaSALComputo } from "../utils/calcoloSAL";
import { economiaSintetica, mappeEconomicheOrg, numIt, costiDaMovimenti, ricaviCommessa, sommaCostiOda } from "../utils/economiaCommessa";

function fmt(n) {
  return (parseFloat(n)||0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtPerc1(n) {
  return (parseFloat(n)||0).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

// Scostamenti margine previsto/reale, aggregati per cliente — usa lo stesso collegamento
// offerta↔commessa già costruito dentro Elaborazione Offerte (nessun dato nuovo richiesto
// all'utente, solo mettere in fila quello che già esiste).
// Tempo medio da Offerta Finalizzata a Commessa avviata — il ciclo commerciale reale,
// calcolato da solo dalle date che già esistono, non stimato.
function TempoMedioOffertaCommessa({ orgId }) {
  const [giorniMedi, setGiorniMedi] = useState(null);
  const [nCampioni, setNCampioni] = useState(0);
  const [caricato, setCaricato] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const offSnap = await getDocs(query(collection(db,"elaborazioniOfferte"), where("orgId","==",orgId)));
      const collegate = offSnap.docs.map(d=>({id:d.id,...d.data()})).filter(e=>e.projectIdCollegato && e.dataEmissione);
      if (collegate.length===0) { setCaricato(true); return; }

      const projSnap = await getDocs(query(collection(db,"projects"), where("organizationId","==",orgId)));
      const progettiMappa = Object.fromEntries(projSnap.docs.map(d=>[d.id,d.data()]));

      const differenze = collegate.map(e => {
        const progetto = progettiMappa[e.projectIdCollegato];
        const dataCommessa = progetto?.createdAt?.toDate?.();
        if (!dataCommessa) return null;
        const dataOfferta = new Date(e.dataEmissione+"T00:00:00");
        const giorni = Math.round((dataCommessa-dataOfferta)/86400000);
        return giorni>=0 ? giorni : null; // scarto eventuali dati incoerenti (commessa precedente all'offerta)
      }).filter(g=>g!==null);

      if (differenze.length>0) {
        setGiorniMedi(differenze.reduce((s,g)=>s+g,0)/differenze.length);
        setNCampioni(differenze.length);
      }
      setCaricato(true);
    })();
  }, [orgId]);

  if (!caricato || giorniMedi===null) return null;

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginBottom:20}}>
      <h3 style={{fontSize:14,fontWeight:700,color:"#1e1b4b",marginBottom:4}}>⏱️ Tempo medio da Offerta a Commessa</h3>
      <p style={{fontSize:11.5,color:"#6b7280",marginBottom:10}}>Il tuo ciclo commerciale reale, calcolato da {nCampioni} offerta/e collegata/e a una commessa.</p>
      <div style={{fontSize:28,fontWeight:900,color:"#7c3aed"}}>{Math.round(giorniMedi)} <span style={{fontSize:14,fontWeight:600,color:"#6b7280"}}>giorni in media</span></div>
    </div>
  );
}

function ScostamentiMargine({ orgId }) {
  const [righe, setRighe] = useState([]);
  const [caricato, setCaricato] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const snap = await getDocs(query(collection(db,"elaborazioniOfferte"), where("orgId","==",orgId)));
      const collegate = snap.docs.map(d=>({id:d.id,...d.data()})).filter(e=>e.projectIdCollegato);
      const ordiniOrg = (await getDocs(query(collection(db,"ordiniAcquisto"), where("organizationId","==",orgId)))).docs.map(d=>d.data());
      const risultati = await Promise.all(collegate.map(async (e) => {
        const [budgetSnap, movSnap] = await Promise.all([
          getDocs(query(collection(db,"budgetCommessa"), where("projectId","==",e.projectIdCollegato), where("organizationId","==",orgId))),
          getDocs(query(collection(db,"movimentiEconomici"), where("projectId","==",e.projectIdCollegato), where("organizationId","==",orgId))),
        ]);
        // Stesse regole del Controllo Economico (utils/economiaCommessa.js)
        const { ricavi, costi } = economiaSintetica({ budget: budgetSnap.empty?null:budgetSnap.docs[0].data(), movimenti: movSnap.docs.map(d=>d.data()), ordini: ordiniOrg, projectId: e.projectIdCollegato });
        if (ricavi<=0) return null;
        const margineReale = (ricavi-costi)/ricavi*100;
        const totali = calcolaTotaliEO(e);
        const marginePrevisto = totali.totaleGenerale>0 ? (totali.totaleMargini/totali.totaleGenerale*100) : 0;
        return { clienteNome: e.clienteNome||"—", marginePrevisto, margineReale, scostamento: marginePrevisto-margineReale };
      }));
      setRighe(risultati.filter(Boolean));
      setCaricato(true);
    })();
  }, [orgId]);

  if (!caricato || righe.length===0) return null;

  const perCliente = {};
  righe.forEach(r => {
    if (!perCliente[r.clienteNome]) perCliente[r.clienteNome] = [];
    perCliente[r.clienteNome].push(r.scostamento);
  });
  const aggregato = Object.entries(perCliente).map(([cliente, scostamenti]) => ({
    cliente, n: scostamenti.length,
    media: scostamenti.reduce((s,v)=>s+v,0)/scostamenti.length,
  })).sort((a,b)=>Math.abs(b.media)-Math.abs(a.media));

  const mediaGenerale = righe.reduce((s,r)=>s+r.scostamento,0)/righe.length;

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginBottom:20}}>
      <h3 style={{fontSize:14,fontWeight:700,color:"#1e1b4b",marginBottom:4,display:"flex",alignItems:"center",gap:6}}>
        <AlertTriangle size={15} color="#d97706"/> Scostamenti Margine Previsto/Reale
      </h3>
      <p style={{fontSize:11.5,color:"#6b7280",marginBottom:14}}>
        Su {righe.length} offerta/e collegata/e a una commessa · in media {mediaGenerale>=0?"sottostimi":"sovrastimi"} il margine di <strong style={{color:mediaGenerale>=0?"#dc2626":"#059669"}}>{fmtPerc1(Math.abs(mediaGenerale))} punti</strong>
      </p>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{borderBottom:"2px solid #f3f4f6"}}>
          {["Cliente","Offerte confrontate","Scostamento medio"].map(h=>(
            <th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {aggregato.map((a,i) => (
            <tr key={a.cliente} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6"}}>
              <td style={{padding:"7px 8px",color:"#111827",fontWeight:600}}>{a.cliente}</td>
              <td style={{padding:"7px 8px",color:"#374151"}}>{a.n}</td>
              <td style={{padding:"7px 8px",fontWeight:700,color:a.media>=0?"#dc2626":"#059669"}}>
                {a.media>=0?"−":"+"}{fmtPerc1(Math.abs(a.media))} punti {a.media>=0?"(sottostimato)":"(sovrastimato)"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Maturato non ancora richiesto — lavoro già fatto (SAL maturato) che nessuno ha ancora
// chiesto di fatturare: maturato − fatturato − richieste di fatturazione in attesa. Le
// richieste in attesa sono già nel riquadro «Da fatturare» di Insight: qui non si ripetono
// (utils/fatturazioneCommessa.js riporta fatturato e richieste in attesa sul SAL), per commessa. Nessun giudizio sui tempi di pagamento del cliente (quello
// Nexiora non lo sa): solo cosa resta da fatturare da parte nostra.
function CreditiInSospeso({ orgId }) {
  const [righe, setRighe] = useState([]);
  const [caricato, setCaricato] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const snap = await getDocs(query(collection(db,"computiSAL"), where("orgId","==",orgId)));
      const risultati = snap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(s => (s.periodi||[]).length>0)
        .map(s => {
          const dati = calcolaSALComputo(s);
          const ultimo = s.periodi[s.periodi.length-1];
          const giorniFermo = Math.round((new Date()-new Date(ultimo.data+"T00:00:00"))/86400000);
          return { commessa:s.projectName||"—", cliente:s.clienteNome||"—", daFatturare:dati.nonRichiesto, inAttesa:dati.richiestoInAttesa, giorniFermo };
        })
        .filter(r => r.daFatturare>0)
        .sort((a,b)=>b.daFatturare-a.daFatturare);
      setRighe(risultati);
      setCaricato(true);
    })();
  }, [orgId]);

  if (!caricato || righe.length===0) return null;
  const totale = righe.reduce((s,r)=>s+r.daFatturare,0);

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginBottom:20}}>
      <h3 style={{fontSize:14,fontWeight:700,color:"#1e1b4b",marginBottom:4}}>💰 Maturato non ancora richiesto in fattura</h3>
      <p style={{fontSize:11.5,color:"#6b7280",marginBottom:14}}>
        Lavoro già maturato nei SAL che nessuno ha ancora chiesto di fatturare — totale <strong style={{color:"#d97706"}}>€{fmt(totale)}</strong> su {righe.length} commess{righe.length===1?"a":"e"}. Le richieste già inviate all'amministrazione sono in «Da fatturare» e qui non si contano.
      </p>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{borderBottom:"2px solid #f3f4f6"}}>
          {["Commessa","Cliente","Da chiedere in fattura","Ultimo SAL"].map(h=>(
            <th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {righe.map((r,i) => (
            <tr key={i} style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6"}}>
              <td style={{padding:"7px 8px",fontWeight:600,color:"#111827"}}>{r.commessa}</td>
              <td style={{padding:"7px 8px",color:"#374151"}}>{r.cliente}</td>
              <td style={{padding:"7px 8px",fontWeight:700,color:"#d97706"}}>€{fmt(r.daFatturare)}</td>
              <td style={{padding:"7px 8px",color:r.giorniFermo>30?"#dc2626":"#6b7280"}}>{r.giorniFermo}gg fa{r.giorniFermo>30?" ⚠️":""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STATO_COLORI = {
  bozza: {bg:"#f3f4f6",col:"#6b7280",label:"Bozza"},
  finalizzata: {bg:"#d1fae5",col:"#065f46",label:"Finalizzata"},
  emessa: {bg:"#ede9fe",col:"#5b21b6",label:"Emessa"},
  annullata: {bg:"#fee2e2",col:"#991b1b",label:"Annullata"},
};
const MESI_FILTRO = ["01-Gen","02-Feb","03-Mar","04-Apr","05-Mag","06-Giu","07-Lug","08-Ago","09-Set","10-Ott","11-Nov","12-Dic"];

export default function SezioneCommerciale({ onNavigate, clientiInattivi = null }) {
  const { effectiveOrgId: orgId } = useOrg();
  const [elaborazioni, setElaborazioni] = useState([]);
  const [anno, setAnno] = useState("");
  const [mese, setMese] = useState("");
  const [clienteFiltro, setClienteFiltro] = useState("");
  const [statoFiltro, setStatoFiltro] = useState("");

  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db,"elaborazioniOfferte"), where("orgId","==",orgId)),
      snap => setElaborazioni(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId]);

  const anniDisponibili = [...new Set(elaborazioni.map(e=>e.dataEmissione?.split("-")[0]).filter(Boolean))].sort((a,b)=>b-a);
  const clientiDisponibili = [...new Set(elaborazioni.map(e=>e.clienteNome).filter(Boolean))].sort((a,b)=>a.localeCompare(b,"it"));

  const delPeriodo = elaborazioni.filter(e => {
    if (anno && e.dataEmissione?.split("-")[0]!==anno) return false;
    if (mese && e.dataEmissione?.split("-")[1]!==mese) return false;
    if (clienteFiltro && e.clienteNome!==clienteFiltro) return false;
    if (statoFiltro && e.stato!==statoFiltro) return false;
    return true;
  });

  // Il dato che conta davvero: margine reale sulle offerte finalizzate — calcolato dal vivo,
  // mai da un campo salvato che potrebbe mancare sulle offerte più vecchie.
  const finalizzate = delPeriodo.filter(e=>e.stato==="finalizzata");
  const finalizzateConTotali = finalizzate.map(e=>calcolaTotaliEO(e));
  const valoreFinalizzate = finalizzateConTotali.reduce((s,t)=>s+t.totaleGenerale,0);
  const margineFinalizzate = finalizzateConTotali.reduce((s,t)=>s+t.totaleMargini,0);
  const margineFinalizzatePerc = valoreFinalizzate>0 ? (margineFinalizzate/valoreFinalizzate*100) : null;

  const inCorso = delPeriodo.filter(e => e.stato==="bozza"||e.stato==="emessa");
  const valoreTotale = delPeriodo.reduce((s,e)=>s+(parseFloat(e.totaleGenerale)||0),0);

  const inp = {padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"};
  const filtriAttivi = anno||mese||clienteFiltro||statoFiltro;

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6,flexWrap:"wrap"}}>
        <h3 style={{fontSize:16,fontWeight:800,color:"#1e1b4b",margin:0,display:"flex",alignItems:"center",gap:8}}>
          <TrendingUp size={18} color="#7c3aed"/> Sezione Commerciale
        </h3>
      </div>
      <p style={{fontSize:12,color:"#6b7280",marginBottom:14}}>Elaborazione e Emissione Offerte · dati riservati alla gestione dell'organizzazione</p>

      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:18,alignItems:"center"}}>
        <select value={clienteFiltro} onChange={e=>setClienteFiltro(e.target.value)} style={inp}>
          <option value="">Tutti i clienti</option>
          {clientiDisponibili.map(c=><option key={c} value={c}>{c}</option>)}
        </select>
        <select value={mese} onChange={e=>setMese(e.target.value)} style={inp}>
          <option value="">Tutti i mesi</option>
          {MESI_FILTRO.map(m=><option key={m} value={m.slice(0,2)}>{m.slice(3)}</option>)}
        </select>
        <select value={anno} onChange={e=>setAnno(e.target.value)} style={inp}>
          <option value="">Tutti gli anni</option>
          {anniDisponibili.map(a=><option key={a} value={a}>{a}</option>)}
        </select>
        <select value={statoFiltro} onChange={e=>setStatoFiltro(e.target.value)} style={inp}>
          <option value="">Tutti gli stati</option>
          {Object.entries(STATO_COLORI).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
        </select>
        {filtriAttivi && (
          <button onClick={()=>{setAnno("");setMese("");setClienteFiltro("");setStatoFiltro("");}}
            style={{padding:"6px 12px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",fontSize:12,cursor:"pointer"}}>
            × Reset
          </button>
        )}
      </div>

      <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:24}}>
        <div style={{background:"#fff",border:"2px solid #059669",borderRadius:14,padding:"16px 18px",flex:1,minWidth:220}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <TrendingUp size={15} color="#059669"/>
            <span style={{fontSize:11,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>💰 Margine nelle offerte finalizzate</span>
          </div>
          <div style={{display:"flex",alignItems:"baseline",gap:12}}>
            <div style={{fontSize:22,fontWeight:800,color:"#059669"}}>{margineFinalizzatePerc===null?"—":`${(margineFinalizzatePerc).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%`}</div>
            <div style={{fontSize:22,fontWeight:800,color:"#059669"}}>{finalizzate.length===0?"":`€ ${fmt(margineFinalizzate)}`}</div>
          </div>
          <div style={{fontSize:11,color:"#1f2937",marginTop:2}}>{finalizzate.length===0?"nessuna offerta finalizzata":`su ${finalizzate.length} offerta/e, valore totale € ${fmt(valoreFinalizzate)}`}</div>
        </div>
        <div onClick={()=>onNavigate && onNavigate({ page:"elaborazione-offerte", filtroStato:"inCorso" })} title="Apri le offerte in corso (bozze ed emesse)"
          style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:"16px 18px",flex:1,minWidth:180,cursor:onNavigate?"pointer":"default"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <FileText size={15} color="#7c3aed"/>
            <span style={{fontSize:11,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>Offerte in corso</span>
          </div>
          <div style={{fontSize:22,fontWeight:800,color:"#111827"}}>{inCorso.length}</div>
          <div style={{fontSize:11,color:"#1f2937",marginTop:2}}>ancora in bozza o già emesse{onNavigate ? " · apri ›" : ""}</div>
        </div>
        <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:"16px 18px",flex:1,minWidth:180}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <TrendingUp size={15} color="#059669"/>
            <span style={{fontSize:11,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>Valore periodo</span>
          </div>
          <div style={{fontSize:22,fontWeight:800,color:"#111827"}}>€ {fmt(valoreTotale)}</div>
          <div style={{fontSize:11,color:"#1f2937",marginTop:2}}>su {delPeriodo.length} elaborazione/i in totale</div>
        </div>
        {/* Da Nexiora Insight (ottobre 2026): prima era una scheda per ogni cliente */}
        {clientiInattivi !== null && (
          <div onClick={()=>onNavigate && onNavigate({page:"clienti"})} title="Clienti con commesse in passato ma nessuna nuova negli ultimi 90 giorni"
            style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:"16px 18px",flex:1,minWidth:180,cursor:"pointer"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              <span style={{fontSize:11,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>Clienti da ricontattare</span>
            </div>
            <div style={{fontSize:22,fontWeight:800,color:"#111827"}}>{clientiInattivi}</div>
            <div style={{fontSize:11,color:"#1f2937",marginTop:2}}>nessuna nuova commessa da 90 giorni</div>
          </div>
        )}
      </div>

      <TempoMedioOffertaCommessa orgId={orgId}/>
      <ScostamentiMargine orgId={orgId}/>
      <CreditiInSospeso orgId={orgId}/>

      <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:14,padding:20,marginBottom:20}}>
        <h3 style={{fontSize:14,fontWeight:700,color:"#1e1b4b",marginBottom:4}}>📋 Elenco elaborazioni {filtriAttivi?"filtrate":""}</h3>
        <p style={{fontSize:11.5,color:"#6b7280",marginBottom:14}}>{delPeriodo.length} elaborazione/i — clicca una riga per aprirla</p>
        {delPeriodo.length===0 ? (
          <div style={{textAlign:"center",padding:40,color:"#6b7280"}}>
            <div style={{fontSize:40,marginBottom:10,opacity:.15}}>📊</div>
            <div style={{fontSize:13,fontWeight:600}}>Nessuna elaborazione con questi filtri</div>
          </div>
        ) : (
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{borderBottom:"2px solid #f3f4f6"}}>
              {["N°","Oggetto","Cliente","Data","Stato","Totale","Margine % (previsto)"].map(h=>(
                <th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:9.5,fontWeight:700,color:"#374151",textTransform:"uppercase"}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {delPeriodo.map((e,i) => {
                const st = STATO_COLORI[e.stato]||STATO_COLORI.bozza;
                const totaliRiga = calcolaTotaliEO(e);
                const margPerc = totaliRiga.totaleGenerale>0 ? (totaliRiga.totaleMargini/totaliRiga.totaleGenerale*100) : null;
                return (
                  <tr key={e.id} onClick={()=>onNavigate&&onNavigate({page:"elaborazione-offerte", eoId:e.id})}
                    style={{background:i%2===0?"#fff":"#fafafa",borderBottom:"1px solid #f3f4f6",cursor:"pointer"}}>
                    <td style={{padding:"7px 8px",fontFamily:"monospace",fontWeight:700,color:"#d97706"}}>{e.numero||"—"}</td>
                    <td style={{padding:"7px 8px",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.oggetto||"—"}</td>
                    <td style={{padding:"7px 8px",color:"#374151"}}>{e.clienteNome||"—"}</td>
                    <td style={{padding:"7px 8px",color:"#374151",whiteSpace:"nowrap"}}>{e.dataEmissione?e.dataEmissione.split("-").reverse().join("/"):"—"}</td>
                    <td style={{padding:"7px 8px"}}><span style={{fontSize:10,padding:"2px 7px",borderRadius:8,background:st.bg,color:st.col,fontWeight:700}}>{st.label}</span></td>
                    <td style={{padding:"7px 8px",fontWeight:700,color:"#059669"}}>€{fmt(totaliRiga.totaleGenerale)}</td>
                    <td style={{padding:"7px 8px",fontWeight:700,color:margPerc===null?"#d1d5db":margPerc<10?"#dc2626":"#7c3aed"}}>{margPerc===null?"—":`${(margPerc).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
